import {
  Autocomplete as MUIAutocomplete,
  AutocompleteRenderOptionState,
  ListItemText,
  MenuItem,
} from '@mui/material';
import classNames from 'classnames';
import React, {
  HTMLAttributes,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Option } from 'store/options';
import TextField from 'components/TextField';
import { FormattedMessage } from 'react-intl';
import Tooltip from 'components/Tooltip';
import { CloseIcon } from 'components/Icons';
import TrimmedList from 'components/TrimmedList/TrimmedList.component';
import {
  DrowpdownCheckbox,
  List,
  TagWrapper,
  TooltipChip,
  Wrapper,
  Level,
} from './MultiselectAutocomplete.styled';
import { MultiselectAutocompleteProps } from './interfaces';

function MultiselectAutocomplete<T extends Option>({
  options,
  size = 'large',
  placeholder,
  label,
  ListboxProps = {},
  selectedOptions,
  removeSelection = () => {},
  className,
  customTagsMessage,
  showSelectedOptionsNames,
  optionMaxWidth = 120,
  isInModal,
  renderOption,
  disableClearButton = false,
  onSearch,
  ...props
}: MultiselectAutocompleteProps<T> & {
  onSearch?: (searchText: string) => void;
}) {
  const [inputClosed, setInputClosed] = useState(true);
  const [inputValue, setInputValue] = useState('');

  const renderOptionFunction = useCallback(
    (
      optionProps: HTMLAttributes<HTMLLIElement>,
      option: T,
      { selected }: AutocompleteRenderOptionState,
    ) => (
      <MenuItem
        disabled={option.disabled}
        {...optionProps}
        key={option.id || option.name}
      >
        <Level level={option.level ?? 0} />
        <DrowpdownCheckbox disabled={option.disabled} checked={selected} />
        <ListItemText primary={option.name} />
      </MenuItem>
    ),
    [],
  );

  const isOptionEqualToValue = useCallback(
    (option: T, optionValue: T) => option.id === optionValue.id,
    [],
  );

  const renderTags = (selected: T[]) => (
    <TagWrapper className="tag-wrapper">
      {selected.length > 1
        ? // show in form of "option 1, option 2, + 2"
          (showSelectedOptionsNames && (
            <TrimmedList
              itemWidth={optionMaxWidth}
              items={selected.map(({ name }) => name)}
            />
          )) || (
            <FormattedMessage
              id={
                customTagsMessage?.defaultMessage ||
                'common.autocomplete.selectedItems'
              }
              defaultMessage={
                customTagsMessage?.defaultMessage ||
                '{count, plural, one {# item} other {# items}} selected'
              }
              values={{ count: selected.length }}
            />
          )
        : // If there is only one item selected, display the item name.
          selected[0].name}
    </TagWrapper>
  );

  const selectedOptionsList = useMemo(
    () =>
      options
        .filter(({ id }) => selectedOptions?.includes(id))
        .map(({ name, id, disabled }) => (
          <TooltipChip
            key={id}
            label={name}
            onMouseDown={(e: SyntheticEvent) => e.stopPropagation()}
            onDelete={inputClosed ? () => removeSelection(id) : undefined}
            deleteIcon={<CloseIcon />}
            disabled={disabled}
          />
        )),
    [options, removeSelection, selectedOptions, inputClosed],
  );

  const onFocus = useCallback(() => setInputClosed(false), []);
  const onBlur = useCallback(() => {
    setInputValue('');
    setTimeout(() => setInputClosed(true), 300);
  }, []);

  const onInputChange = useCallback(
    (e: SyntheticEvent, value: string, reason: string) => {
      if (reason === 'input' || reason === 'clear') {
        setInputValue(value);
        onSearch?.(value);
      }

      if (reason === 'clear') {
        e.stopPropagation();
      }
    },
    [onSearch],
  );

  const showTooltip =
    (inputClosed && selectedOptionsList.length > 1) ||
    (!inputClosed && selectedOptionsList.length);

  return (
    <Wrapper className={className}>
      <Tooltip
        variant="light"
        className="tooltip"
        placement={inputClosed ? 'bottom' : 'right'}
        title={selectedOptionsList}
        disableHoverListener={!inputClosed}
        componentsProps={{
          tooltip: {
            variant: 'light',
            style: {
              marginTop: inputClosed ? '10px' : '25px',
              display: showTooltip ? 'block' : 'none',
              visibility: showTooltip ? 'visible' : 'hidden',
              width: '100%',
            },
          },
          popper: {
            style: {
              zIndex: isInModal ? 10000 : 999,
              padding: inputClosed ? '0 20px 20px 20px' : 'unset',
            },
          },
        }}
      >
        <MUIAutocomplete
          {...props}
          multiple
          disableCloseOnSelect
          inputValue={inputValue}
          onInputChange={onInputChange}
          blurOnSelect={false}
          onBlur={onBlur}
          onFocus={onFocus}
          disableClearable={disableClearButton}
          isOptionEqualToValue={isOptionEqualToValue}
          renderOption={renderOption || renderOptionFunction}
          renderTags={renderTags}
          options={options}
          ListboxProps={{
            ...ListboxProps,
            className: classNames(
              ListboxProps.className,
              `SelectMenuSize-${size}`,
            ),
          }}
          ListboxComponent={List}
          renderInput={(textFieldProps) => (
            <TextField
              {...textFieldProps}
              placeholder={selectedOptions?.length ? '' : placeholder}
              size={size}
              label={label}
            />
          )}
          componentsProps={{
            paper: {
              sx: {
                minWidth: 'fit-content',
              },
            },
            popper: {
              sx: {
                zIndex: isInModal ? 10000 : 999,
              },
            },
          }}
        />
      </Tooltip>
    </Wrapper>
  );
}

MultiselectAutocomplete.MultiselectAutocomplete = {
  disablePortal: true,
};

export default MultiselectAutocomplete;
